import { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Spin, notification, Tooltip } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { apiInvitationLinkDetails } from "../../../../api/invitationLink";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect/AccountsSearchSelect";
import PlansSearchSelect from "../../../common/PlansSearchSelect/PlansSearchSelect";
import { userHasPermission } from "../../../../helpers/user";
import "./InvitationLinkModal.scss";

const InvitationLinkModal = ({
  isModalVisible,
  handleOk,
  handleEdit,
  handleDelete,
  handleCancel,
  form,
  loading,
  linkInfo,
}) => {
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [registrationUrl, setRegistrationUrl] = useState("");

  // Store selected values as objects for the search selects
  const [agent, setAgent] = useState(null);
  const [n1Distributor, setN1Distributor] = useState(null);
  const [n2Distributor, setN2Distributor] = useState(null);
  const [plan, setPlan] = useState(null);
  const [parent, setParent] = useState(null);

  useEffect(() => {
    const fetchLinkDetails = async () => {
      if (!linkInfo) {
        form.resetFields();
        setRegistrationUrl("");
        setAgent(null);
        setN1Distributor(null);
        setN2Distributor(null);
        setPlan(null);
        setParent(null);
        return;
      }

      try {
        setLoadingDetails(true);
        const data = await apiInvitationLinkDetails(linkInfo.id);

        form.setFieldsValue({ name: data.name });
        setAgent(data.agent);
        setN1Distributor(data.n1_distributor);
        setN2Distributor(data.n2_distributor);
        setPlan(data.plan);
        setParent(data.parent);
        setRegistrationUrl(`${window.location.origin}/register/${data.short_id}`);
      } catch (error) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao carregar os detalhes do link!",
          placement: "topRight",
        });
      } finally {
        setLoadingDetails(false);
      }
    };

    if (isModalVisible) {
      fetchLinkDetails();
    }
  }, [linkInfo, form, isModalVisible]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(registrationUrl).then(() => {
      notification.success({
        message: "Sucesso",
        description: "Link copiado com sucesso!",
        placement: "topRight",
      });
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        agent: agent?.id ?? null,
        n1_distributor: n1Distributor?.id ?? null,
        n2_distributor: n2Distributor?.id ?? null,
        plan: plan?.id ?? null,
        parent: parent?.id ?? null,
      };

      if (linkInfo) {
        handleEdit(linkInfo.id, payload);
      } else {
        handleOk(payload);
      }
    } catch (error) {
      // Form validation error
    }
  };

  const onCancel = () => {
    setAgent(null);
    setN1Distributor(null);
    setN2Distributor(null);
    setPlan(null);
    setParent(null);
    handleCancel();
  };

  return (
    <Modal
      title={linkInfo ? "Editar Link de Convite" : "Criar Link de Convite"}
      open={isModalVisible}
      onCancel={onCancel}
      width={600}
      footer={[
        linkInfo && (
          <Button
            key="delete"
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(linkInfo.id)}
          >
            Deletar
          </Button>
        ),
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading || loadingDetails}
          onClick={handleSubmit}
        >
          {linkInfo ? "Atualizar" : "Criar"}
        </Button>,
      ]}
    >
      <Spin spinning={loadingDetails} tip="Carregando...">
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nome"
            name="name"
            rules={[{ required: true, message: "Nome é obrigatório!" }]}
          >
            <Input placeholder="Nome do link de convite" />
          </Form.Item>

          {userHasPermission("register_account_choose_agent") && (
            <Form.Item label="Agente">
              <AccountsSearchSelect
                value={agent}
                onChange={setAgent}
                placeholder="Pesquise e selecione um agente"
                allowClear
              />
            </Form.Item>
          )}

          {userHasPermission("register_account_choose_n1") && (
            <Form.Item label="Distribuidor N1">
              <AccountsSearchSelect
                value={n1Distributor}
                onChange={setN1Distributor}
                placeholder="Pesquise e selecione um distribuidor N1"
                allowClear
              />
            </Form.Item>
          )}

          {userHasPermission("register_account_choose_n2") && (
            <Form.Item label="Distribuidor N2">
              <AccountsSearchSelect
                value={n2Distributor}
                onChange={setN2Distributor}
                placeholder="Pesquise e selecione um distribuidor N2"
                allowClear
              />
            </Form.Item>
          )}

          {userHasPermission("register_account_choose_plan") && (
            <Form.Item label="Plano">
              <PlansSearchSelect
                value={plan}
                onChange={setPlan}
                placeholder="Pesquise e selecione um plano"
                allowClear
              />
            </Form.Item>
          )}

          {userHasPermission("register_account_sub") && (
            <Form.Item label="Conta Pai (Conta Virtual)">
              <AccountsSearchSelect
                value={parent}
                onChange={setParent}
                placeholder="Pesquise e selecione uma conta pai"
                allowClear
              />
            </Form.Item>
          )}

          {linkInfo && registrationUrl && (
            <Form.Item label="URL de Registro">
              <div className="url-copy-container">
                <Input value={registrationUrl} readOnly />
                <Tooltip title="Copiar URL">
                  <Button icon={<CopyOutlined />} onClick={handleCopyUrl} />
                </Tooltip>
              </div>
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default InvitationLinkModal;
